import { apiClient } from '@/api/client';
import { uploadDirect } from '@/api/direct-upload.api';

/*
 * Mirror of the API's `src/chat/chat.types.ts`. Change both.
 *
 * No call here sends a clinic or a sender: the server takes both from the
 * session, so a message can only ever be written as the signed-in user.
 */

export type ChatRefType =
  | 'patient'
  | 'appointment'
  | 'lab_request'
  | 'document'
  | 'prescription'
  | 'treatment_plan'
  | 'procedure'
  | 'consent_form'
  | 'insurance_claim'
  | 'invoice'
  | 'material';

export const CHAT_REF_TYPES: ChatRefType[] = [
  'patient',
  'appointment',
  'lab_request',
  'document',
  'prescription',
  'treatment_plan',
  'procedure',
  'consent_form',
  'insurance_claim',
  'invoice',
  'material',
];

export type ChatPriority = 'normal' | 'urgent';
export type ChatStockLevel = 'ok' | 'low' | 'critical' | 'out';

export interface ChatRefInput {
  type: ChatRefType;
  id: string;
}

/** A shared record, as the card draws it. Absent fields when `available` is false. */
export interface ChatRef extends ChatRefInput {
  available: boolean;
  patientId?: string | null;
  patientName?: string | null;
  patientCode?: string | null;
  phone?: string | null;
  scheduledAt?: string | null;
  doctorName?: string | null;
  status?: string | null;
  requestType?: string | null;
  labName?: string | null;
  dueDate?: string | null;
  fileName?: string | null;
  tag?: string | null;
  mime?: string | null;
  url?: string | null;
  /** Plan, procedure, consent or material name, as stored. */
  title?: string | null;
  /** Claim or invoice number. */
  number?: string | null;
  issuedAt?: string | null;
  /** procedure: the appointment its page lives under. */
  reservationId?: string | null;
  toothNumber?: string | null;
  itemCount?: number | null;
  doneCount?: number | null;
  acceptance?: string | null;
  summary?: string | null;
  brand?: string | null;
  unit?: string | null;
  quantity?: number | null;
  stockLevel?: ChatStockLevel | null;
}

export interface ChatFileInput {
  key: string;
  name: string;
  mime: string;
  size: number;
}

export interface ChatFile extends ChatFileInput {
  /** Short-lived signed link; null when it could not be signed. */
  url: string | null;
}

export interface ChatReply {
  id: string;
  senderId: string;
  content: string;
  deleted: boolean;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  createdAt: string;
  deleted: boolean;
  /** When the text was last changed, or null. */
  editedAt: string | null;
  priority: ChatPriority;
  /** Staff ids called out with "@name". */
  mentionIds: string[];
  /** "@everyone" was used. */
  mentionsAll: boolean;
  ref: ChatRef | null;
  replyTo: ChatReply | null;
  files: ChatFile[];
  clientId?: string | null;
  /** Client-only: shown at once, not yet confirmed by the server. */
  pending?: boolean;
  /** Client-only: the send was refused or never arrived. */
  failed?: boolean;
}

export interface ChatLastMessage {
  id: string;
  senderId: string;
  content: string;
  createdAt: string;
  deleted: boolean;
  refType: ChatRefType | null;
  fileCount: number;
  priority: ChatPriority;
}

export interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name: string | null;
  participantIds: string[];
  createdBy: string | null;
  lastMessageAt: string | null;
  lastMessage: ChatLastMessage | null;
  unreadCount: number;
  /** Unread messages that mention me (by name or "@everyone"). */
  mentionCount: number;
  /** Unread messages sent as urgent. */
  urgentCount: number;
  /** I silenced it: no sound, toast or badge, except mentions and urgent. */
  muted: boolean;
  /** Until when; null while muted is "until turned back on". */
  mutedUntil: string | null;
  /** Staff id → ISO time they have read up to. */
  reads: Record<string, string>;
}

export interface ChatContact {
  id: string;
  name: string;
  clinicRole: string | null;
  specialty: string | null;
  isActive: boolean;
}

export interface ChatMessagePage {
  items: ChatMessage[];
  hasMore: boolean;
}

export interface SendMessagePayload {
  content?: string;
  ref?: ChatRefInput;
  files?: ChatFileInput[];
  replyToId?: string;
  clientId?: string;
  mentionIds?: string[];
  mentionAll?: boolean;
  priority?: ChatPriority;
}

export interface EditMessagePayload {
  content: string;
  mentionIds?: string[];
  mentionAll?: boolean;
}

export interface ChatSearchHit {
  message: ChatMessage;
  conversationId: string;
}

export const CHAT_MESSAGE_MAX_LENGTH = 4000;
export const CHAT_MAX_FILES = 5;
export const CHAT_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const CHAT_FILE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
];
export const CHAT_PAGE_SIZE = 40;
/** Mirror of the API's `CHAT_EDIT_WINDOW_MS`: a sent text can be fixed for this long. */
export const CHAT_EDIT_WINDOW_MS = 15 * 60 * 1000;
export const CHAT_SEARCH_MIN_LENGTH = 2;
/** Mute choices, in hours; null is "until I turn it back on". */
export const CHAT_MUTE_HOURS = [1, 8, 24 * 7, null] as const;
export type ChatMuteHours = (typeof CHAT_MUTE_HOURS)[number];

export const chatApi = {
  async getConversations() {
    const response = await apiClient.get<Conversation[]>('/chat/conversations');
    return response.data;
  },

  async getContacts() {
    const response = await apiClient.get<{
      contacts: ChatContact[];
      online: string[];
    }>('/chat/contacts');
    return response.data;
  },

  async getMessages(conversationId: string, before?: string) {
    const response = await apiClient.get<ChatMessagePage>(
      `/chat/conversations/${conversationId}/messages`,
      { params: { limit: CHAT_PAGE_SIZE, ...(before ? { before } : {}) } },
    );
    return response.data;
  },

  async sendMessage(conversationId: string, payload: SendMessagePayload) {
    const response = await apiClient.post<ChatMessage>(
      `/chat/conversations/${conversationId}/messages`,
      payload,
    );
    return response.data;
  },

  /** Writes to a colleague, opening the direct conversation if there is none. */
  async sendDirectMessage(
    payload: SendMessagePayload & { recipientId: string },
  ) {
    const response = await apiClient.post<{
      message: ChatMessage;
      conversation: Conversation;
    }>('/chat/direct-messages', payload);
    return response.data;
  },

  async markRead(conversationId: string) {
    const response = await apiClient.post<{
      conversationId: string;
      lastReadAt: string;
    }>(`/chat/conversations/${conversationId}/read`);
    return response.data;
  },

  async deleteMessage(messageId: string) {
    await apiClient.delete(`/chat/messages/${messageId}`);
  },

  async editMessage(messageId: string, payload: EditMessagePayload) {
    const response = await apiClient.patch<ChatMessage>(
      `/chat/messages/${messageId}`,
      payload,
    );
    return response.data;
  },

  async search(q: string, conversationId?: string) {
    const response = await apiClient.get<{ items: ChatSearchHit[] }>(
      '/chat/search',
      { params: { q, ...(conversationId ? { conversationId } : {}) } },
    );
    return response.data.items;
  },

  async mute(conversationId: string, hours: ChatMuteHours) {
    const response = await apiClient.post<{
      conversationId: string;
      muted: boolean;
      mutedUntil: string | null;
    }>(`/chat/conversations/${conversationId}/mute`, hours ? { hours } : {});
    return response.data;
  },

  async unmute(conversationId: string) {
    const response = await apiClient.delete<{
      conversationId: string;
      muted: boolean;
      mutedUntil: string | null;
    }>(`/chat/conversations/${conversationId}/mute`);
    return response.data;
  },

  async createGroup(payload: { name: string; participantIds: string[] }) {
    const response = await apiClient.post<Conversation>(
      '/chat/groups',
      payload,
    );
    return response.data;
  },

  async renameGroup(conversationId: string, name: string) {
    const response = await apiClient.patch<Conversation>(
      `/chat/conversations/${conversationId}`,
      { name },
    );
    return response.data;
  },

  async addParticipants(conversationId: string, staffIds: string[]) {
    const response = await apiClient.post<Conversation>(
      `/chat/conversations/${conversationId}/participants`,
      { staffIds },
    );
    return response.data;
  },

  /** Removes a member, or leaves when `staffId` is the caller's own. */
  async removeParticipant(conversationId: string, staffId: string) {
    await apiClient.delete(
      `/chat/conversations/${conversationId}/participants/${staffId}`,
    );
  },

  async uploadFile(file: File) {
    const uploadKey = await uploadDirect(file, 'chat-file');
    const response = await apiClient.post<ChatFile>('/chat/uploads', {
      uploadKey,
    });
    return response.data;
  },
};
